<script setup>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DatePicker } from 'v-calendar'
import { useScreens } from 'vue-screen-utils'
import 'v-calendar/style.css'
import { formatTime } from '@/utils/datetime'

const store = useStore()
const settings = computed(() => store.getters['user/getUserSettings'])

const isOpen = ref(false)
const datePicker = ref(null)
const selectedRangeType = ref(null)
const today = new Date(new Date().setHours(23, 59, 59, 999))

const isSameYear = (date1, date2) => {
  if (!date1 || !date2) return false
  return date1.getFullYear() === date2.getFullYear()
}

// Computed property that syncs with Vuex layers.selectedRange
const range = computed({
  get: () => {
    const selectedRange = store.getters['layers/getSelectedRange']
    if (!selectedRange || selectedRange.length < 2) {
      return { start: null, end: null }
    }
    return {
      start: new Date(selectedRange[0]),
      end: new Date(selectedRange[1])
    }
  },
  set: (value) => {
    if (value?.start && value?.end) {
      store.commit('layers/SET_SELECTED_RANGE', [
        value.start.toISOString(),
        value.end.toISOString()
      ])
    } else {
      store.commit('layers/SET_SELECTED_RANGE', [])
    }
  }
})

const startDate = computed(() => {
  if (!range.value.start) return null
  return formatTime(range.value.start, settings.value.tz, isSameYear(range.value.start, range.value.end) ? 'D MMM' : 'D MMM YY')
})

const endDate = computed(() => {
  if (!range.value.end) return null
  return formatTime(range.value.end, settings.value.tz, 'D MMM YY')
})

const setRange = (rangeString) => {
  switch (rangeString) {
    case '24':
      selectedRangeType.value = '24'
      range.value = {
        start: new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1),
        end: today
      }
      break
    case 'week':
      selectedRangeType.value = 'week'
      range.value = {
        start: new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7),
        end: today
      }
      break
    case 'month':
      selectedRangeType.value = 'month'
      range.value = {
        start: new Date(today.getFullYear(), today.getMonth() - 1, today.getDate()),
        end: today
      }
      break
    case 'year':
      selectedRangeType.value = 'year'
      range.value = {
        start: new Date(today.getFullYear() - 1, today.getMonth(), today.getDate()),
        end: today
      }
      break
    case 'reset':
      store.commit('layers/SET_SELECTED_RANGE', [])
      selectedRangeType.value = null
      if (datePicker.value) {
        datePicker.value.move(new Date())
      }
      break
  }

  isOpen.value = false
}

const { mapCurrent } = useScreens({
  xs: '0px',
  sm: '640px',
  md: '768px',
  lg: '1024px',
})
const columns = mapCurrent({ lg: 2 }, 1)
const expanded = mapCurrent({ lg: false }, true)
const disabledDates = ref([{ start: today }])
</script>

<template>
  <Popover :open="isOpen" @update:open="isOpen = $event">
    <PopoverTrigger as-child>
      <Badge class="shadow-sm cursor-pointer hover:bg-gray-100 transition-colors text-xs">
        <span v-if="range.start && range.end">📅 {{ startDate }} - {{ endDate }}</span>
        <span v-else class="text-gray-400">📅 No date range</span>
      </Badge>
    </PopoverTrigger>
    <PopoverContent class="p-0 w-full">
      <div class="p-1 flex flex-wrap space-x-1">
        <Button @click="setRange('24')" variant="outline" size="sm" :class="{'text-primary': selectedRangeType === '24'}">24h</Button>
        <Button @click="setRange('week')" variant="outline" size="sm" :class="{'text-primary': selectedRangeType === 'week'}">Week</Button>
        <Button @click="setRange('month')" variant="outline" size="sm" :class="{'text-primary': selectedRangeType === 'month'}">Month</Button>
        <Button @click="setRange('year')" variant="outline" size="sm" :class="{'text-primary': selectedRangeType === 'year'}">Year</Button>
        <Button @click="setRange('reset')" variant="outline" size="sm" class="text-gray-500">Reset</Button>
      </div>

      <div class="p-1">
        <DatePicker
          class="font-sans"
          ref="datePicker"
          borderless
          trim-weeks
          v-model.range="range"
          :columns="columns"
          :step="1"
          :initial-page-position="-1"
          :expanded="expanded"
          :disabled-dates="disabledDates"
        />
      </div>
    </PopoverContent>
  </Popover>
</template>

<style lang="postcss">
.vc-container {
  --vc-font-family: 'Nunito', sans-serif;
}
</style>
